import { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, AlertCircle, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useFilters } from "@/context/FilterContext";
import { KpiCard } from "@/components/KpiCard";
import { TrendChart } from "@/components/TrendChart";
import { DataTable } from "@/components/DataTable";
import { TopBottomYO } from "@/components/TopBottomYO";
import { LiveDetailDialog } from "@/components/LiveDetailDialog";
import { BreakdownChart } from "@/components/BreakdownChart";

const ALL = "__all";
const REFRESH_MS = 5 * 60 * 1000;

function Pick({ testid, label, value, onChange, options, allLabel }) {
  return (
    <div className="flex min-w-[8.5rem] shrink-0 flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? null : v)}>
        <SelectTrigger data-testid={testid} className="h-9 bg-background text-sm"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function LiveFilters({ f, setF }) {
  const { meta } = useFilters();
  const units = useMemo(() => [...new Set((meta?.units || []).filter((u) => (!f.uid || u.uid === f.uid) && (!f.tipe || u.tipe === f.tipe)).map((u) => u.unit))], [meta, f.uid, f.tipe]);
  if (!meta) return null;
  return (
    <div data-testid="live-filter-bar" className="sticky top-14 z-30 border-b bg-background/95 backdrop-blur-md">
      <div className="flex items-end gap-3 overflow-x-auto px-4 py-3 sm:px-6 lg:px-8">
        <Pick testid="live-uid-select" label="UID" value={f.uid} onChange={(v) => setF({ ...f, uid: v, unit: null })} options={meta.uids || []} allLabel="Semua UID" />
        <Pick testid="live-tipe-select" label="Tipe Project" value={f.tipe} onChange={(v) => setF({ ...f, tipe: v, unit: null })} options={meta.tipes || []} allLabel="Semua Tipe" />
        <Pick testid="live-unit-select" label="Unit / UP3" value={f.unit} onChange={(v) => setF({ ...f, unit: v })} options={units} allLabel={meta.scoped ? "Semua unit saya" : "Semua Unit"} />
      </div>
    </div>
  );
}

function PeriodChip({ text, testid }) {
  const stale = text?.startsWith("data s.d.");
  const none = text === "Belum ada data";
  return (
    <span data-testid={testid} className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${none ? "border-slate-200 bg-slate-50 text-slate-500 dark:bg-slate-800 dark:text-slate-300" : stale ? "border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300" : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"}`}>
      {text}
    </span>
  );
}

export default function LivePage() {
  const [f, setF] = useState({ uid: null, tipe: null, unit: null });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [at, setAt] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setLoading(true);
    return api.get("/live", { params, timeout: 60000 })
      .then((r) => { setData(r.data); setError(null); setAt(new Date()); })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLoading(false));
  }, [f]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div>
      <LiveFilters f={f} setF={setF} />
      <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#F5A623]">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
              Kinerja Operasi · Live
            </p>
            <h1 data-testid="live-title" className="font-heading mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight">Pantauan Bulan Berjalan</h1>
            <p data-testid="live-period-label" className="mt-1 text-sm text-muted-foreground">
              {data ? `${data.month_label} · hari ke-${data.day}${f.unit ? ` · ${f.unit}` : ""}` : "Memuat…"}
            </p>
          </div>
          <div className="flex flex-col items-start gap-1 sm:items-end">
            <Button data-testid="live-refresh-button" variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Muat Ulang
            </Button>
            <p data-testid="live-updated-at" className="text-[11px] text-muted-foreground">
              Sinkron data: {fmtDate(data?.synced_at)} · Layar diperbarui {at ? at.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) : "–"} (otomatis tiap 5 menit)
            </p>
          </div>
        </div>
        {error && <div data-testid="live-error" className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"><AlertCircle className="h-4 w-4" />{error}</div>}
        {!data && !error && <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}</div>}
        {data && (
          <>
            <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 lg:grid-cols-4">
              {data.kpis.map((k, i) => (
                <div key={k.key} className="flex flex-col gap-1.5">
                  <PeriodChip testid={`live-period-${k.key}`} text={k.period} />
                  <KpiCard kpi={k} index={i} prevYear={k.cmp || "bulan lalu"} />
                </div>
              ))}
            </div>
            <div className="grid gap-6 xl:grid-cols-2">
              {data.charts.map((c) => <div key={c.id} data-testid={`live-chart-${c.id}`}><TrendChart trend={c} /></div>)}
            </div>
            <div data-testid="live-rn-jenis"><BreakdownChart breakdown={data.rn_jenis} /></div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Activity className="h-3.5 w-3.5" />RPT/RCT: {data.periods.rpt} · YO: {data.periods.yo} (tidak terfilter Tipe Project) · RN & LU: {data.periods.anomali}
            </div>
            <div className="space-y-3">
              <p data-testid="live-yo-summary" className="text-sm text-muted-foreground">
                Detail YO ({data.periods.yo}): <b className="text-foreground">{data.yo_summary.tercapai}</b> dari {data.yo_summary.petugas} petugas mencapai target 100%
              </p>
              <TopBottomYO rows={data.officers} n={5} />
            </div>
            <DataTable table={data.table} title="Pantauan per UP3 — Capaian per KPI (terburuk di atas)" onRowClick={(r) => setDetail(r.unit)} rowHint="klik baris untuk rincian RN/LU & petugas YO" />
            <p className="text-[11px] text-muted-foreground">Status RPT/RCT/YO: Tercapai bila capaian ≥ 100%. Status RN/LU: Tercapai bila jumlah ≤ bulan lalu pada tanggal yang sama (*).</p>
          </>
        )}
      </div>
      <LiveDetailDialog unit={detail} params={Object.fromEntries(Object.entries({ uid: f.uid, tipe: f.tipe }).filter(([, v]) => v))} onClose={() => setDetail(null)} />
    </div>
  );
}
